import { format } from 'date-fns';
import type { IDeal } from '@/types/api/IDeal';

/** One row of the card's stage-properties block. */
export interface DealStagePropertyRow {
  label: string;
  /** Formatted value, or null when HubSpot has not filled the property. */
  value: string | null;
  /** Spans both grid columns — for values too long to sit beside a sibling. */
  wide: boolean;
}

interface DealStagePropertyDef {
  label: string;
  wide?: boolean;
  read: (deal: IDeal) => string | null;
}

/** A date HubSpot has not filled, or filled with something unparseable, reads as absent. */
function day(raw: string | undefined): string | null {
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : format(parsed, 'dd MMM yyyy');
}

function count(value: number | undefined): string | null {
  return value === undefined ? null : String(value);
}

function text(value: string | undefined): string | null {
  return value ? value : null;
}

/**
 * Every property a stage may declare below, keyed by the short name the stage lists rather than by
 * its wire name — the labels are the card's, not HubSpot's.
 */
const PROPERTY_DEFS = {
  created: { label: 'Created', read: (deal) => day(deal.hubspot_created_at) },
  recDate: { label: 'REC date', wide: true, read: (deal) => day(deal.stage_properties?.rec_date) },
  recDecision: {
    label: 'REC decision',
    wide: true,
    read: (deal) => text(deal.stage_properties?.rec_decision),
  },
  hpcReady: {
    label: 'HPC spots (ready)',
    read: (deal) => count(deal.stage_properties?.hpc_spots_ready),
  },
  hpcFuture: {
    label: 'HPC spots (future)',
    read: (deal) => count(deal.stage_properties?.hpc_spots_future),
  },
  rentFixed: {
    label: 'Fixed / RMG rent',
    read: (deal) => {
      const rent = deal.stage_properties?.fixed_min_guaranteed_rent;
      if (rent === undefined) return null;
      // per charge point per month, which is the unit HubSpot stores and the only one shown
      return `€${rent.toLocaleString('en-GB', { maximumFractionDigits: 2 })} /CP/mo`;
    },
  },
  rentBasis: {
    label: 'Rent calc. basis',
    read: (deal) => text(deal.stage_properties?.variable_rent_calc_basis),
  },
  rentDuration: {
    label: 'Rent duration',
    read: (deal) => {
      const years = deal.stage_properties?.rent_duration_years;
      return years === undefined ? null : `${years} yrs`;
    },
  },
  securedDate: { label: 'Secured date', read: (deal) => day(deal.stage_properties?.secured_date) },
  forecastLease: {
    label: 'Forecast lease sig.',
    read: (deal) => day(deal.stage_properties?.forecasted_lease_signature),
  },
  leaseSignDate: {
    label: 'Lease signed',
    wide: true,
    read: (deal) => day(deal.stage_properties?.lease_signature_date),
  },
  lostReason: {
    label: 'Lost reason',
    wide: true,
    read: (deal) => text(deal.stage_properties?.loss_reason),
  },
} satisfies Record<string, DealStagePropertyDef>;

type PropertyName = keyof typeof PROPERTY_DEFS;

/**
 * Which properties each stage shows, straight from the integration spec's per-stage table. A stage
 * absent here shows no block at all — that is what an unknown or renamed HubSpot stage looks like,
 * and it must not guess a field list.
 *
 * The rent terms repeat across six stages because the spec repeats them: they are agreed once and
 * stay on the card from the committee onwards.
 */
const RENT_TERMS: readonly PropertyName[] = [
  'hpcReady',
  'hpcFuture',
  'rentFixed',
  'rentBasis',
  'rentDuration',
];

const PROPERTIES_BY_STAGE: Readonly<Record<string, readonly PropertyName[]>> = {
  'Identified Lead': ['created'],
  'Activated Lead': ['created'],
  'REC preparation': ['recDate', ...RENT_TERMS],
  'Offer in preparation': ['recDecision', ...RENT_TERMS],
  'Offer sent': ['recDecision', ...RENT_TERMS],
  Negotiations: ['recDecision', ...RENT_TERMS],
  'TS Signed': ['securedDate', 'forecastLease', ...RENT_TERMS],
  'Lease Signed': ['leaseSignDate', ...RENT_TERMS],
  Lost: ['lostReason', 'hpcReady', 'hpcFuture'],
};

/**
 * The rows the deal's stage declares. A declared property HubSpot has not filled keeps its row with
 * a null value: the rep learns the field applies at this stage and is empty, which is a different
 * fact from the field not applying at all.
 */
export function dealStagePropertyRows(deal: IDeal): DealStagePropertyRow[] {
  const names = PROPERTIES_BY_STAGE[deal.stage] ?? [];
  return names.map((name) => {
    const def: DealStagePropertyDef = PROPERTY_DEFS[name];
    return { label: def.label, value: def.read(deal), wide: def.wide ?? false };
  });
}
